import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PlayCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export function Hero() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleStartBuilding = async () => {
    setIsLoading(true);
    try {
      const { site } = await api.createSite();
      navigate(`/build/${site.id}`);
    } catch (error) {
      console.error("Failed to create site:", error);
      // TODO: Show error toast
      setIsLoading(false);
    }
  };

  const scrollToHowItWorks = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-32 pb-16">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid pointer-events-none opacity-30" />
      
      {/* Subtle warm spot */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-radial-glow pointer-events-none opacity-50" />

      <div className="relative z-10 mx-auto max-w-5xl px-6 text-center">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000 ease-out flex flex-col items-center">
          
          <div className="mb-8 flex items-center justify-center gap-2 text-sm font-medium tracking-wide uppercase text-muted-foreground/80 bg-secondary/50 px-4 py-1.5 rounded-full border border-secondary backdrop-blur-sm">
             <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
             <span>For UK Tradespeople</span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium tracking-tight mb-6 text-primary leading-[1.05]">
            The fastest way for <br className="hidden sm:block" />
            <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary to-primary/70">
              tradespeople to get online.
            </span>
          </h1>
          
          <div className="font-display text-xl md:text-2xl lg:text-3xl font-normal text-muted-foreground mb-8 max-w-3xl mx-auto">
            <span className="text-foreground border-b-2 border-accent/20">Professional websites in under 15 minutes.</span> <span className="text-foreground border-b-2 border-accent/20">No tech skills needed.</span>
          </div>

          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed font-light">
            We build your site through a simple chat. You focus on the job, we handle the web.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button 
              size="lg" 
              onClick={handleStartBuilding}
              disabled={isLoading}
              className="h-14 px-8 text-base rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 size-5 animate-spin" />
                  Creating...
                </>
              ) : (
                "Start Building"
              )}
            </Button>
            
            <button
              onClick={scrollToHowItWorks}
              className="group flex items-center gap-2 text-base font-medium text-primary hover:opacity-80 transition-opacity"
            >
              <div className="size-10 rounded-full bg-secondary flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <PlayCircle className="size-5 fill-primary text-primary" />
              </div>
              <span>See how it works</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

